import { type ChangeEvent, type FC, Fragment, useId } from "react"
import styled from "styled-components"

import InputLabel from "~/components/InputLabel"

type DropdownProps = {
	label?: string
	// Consecutive items with the same group ID are shown together under the group's title.
	items: { id: string; title: string; isDisabled?: boolean; group?: { id: string; title: string } }[]
	selectedItemId: string
	onChange: (id: string) => void
	id?: string
	isDisabled?: boolean
	className?: string
}

const Dropdown: FC<DropdownProps> = ({ label, items, selectedItemId, onChange, id, isDisabled, className }) => {
	const fallbackId = useId()
	const selectId = id ?? fallbackId
	const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
		const selectedItem = items.find(item => item.id === event.target.value)
		if (selectedItem === undefined) return
		onChange(selectedItem.id)
	}

	const element = (
		<Container
			value={selectedItemId}
			onChange={handleChange}
			id={selectId}
			disabled={isDisabled}
			className={className}
		>
			{groupItems(items).map(({ group, items }, index) =>
				group === undefined ? (
					<Fragment key={`group-${index}`}>{items.map(renderOption)}</Fragment>
				) : (
					<optgroup key={`group-${index}`} label={group.title}>
						{items.map(renderOption)}
					</optgroup>
				),
			)}
		</Container>
	)

	if (label === undefined) return element

	return (
		<div>
			<InputLabel htmlFor={selectId}>{label}</InputLabel>
			{element}
		</div>
	)
}

function renderOption(item: DropdownProps["items"][number]) {
	return (
		<option key={item.id} value={item.id} disabled={item.isDisabled}>
			{item.title}
		</option>
	)
}

function groupItems(items: DropdownProps["items"]) {
	const groups: { group: DropdownProps["items"][number]["group"]; items: DropdownProps["items"] }[] = []
	for (const item of items) {
		const lastGroup = groups.at(-1)
		if (lastGroup !== undefined && lastGroup.group?.id === item.group?.id) lastGroup.items.push(item)
		else groups.push({ group: item.group, items: [item] })
	}
	return groups
}

const Container = styled.select`
	width: 100%;
	appearance: none;
	background: white;
	background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' height='24px' viewBox='0 0 24 24' width='24px' fill='%23000000'%3E%3Cpath d='M24 24H0V0h24v24z' fill='none' opacity='.87'/%3E%3Cpath d='M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6-1.41-1.41z'/%3E%3C/svg%3E");
	background-repeat: no-repeat;
	background-position-x: calc(100% - 12px);
	background-position-y: center;
	border: 1px solid var(--color-component-border);
	border-radius: 8px;
	transition: border-color 150ms, box-shadow 150ms;

	&:focus-visible {
		outline: none;
		border-color: ${props => props.theme.primaryColor};
		box-shadow: 0 0 0 2px ${props => props.theme.primaryColor}20;
	}

	&:disabled {
		background-color: var(--color-component-background);
		color: var(--color-light);
	}
	padding: 8px ${8 + 24 + 16}px 8px 16px;
`

export default Dropdown
